import React, { Component, Suspense } from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import { AnalyticsProvider } from 'app/scripts/services/analytics-provider';
import { Checkbox } from '../../common/Checkbox/Checkbox';
import ModerationMainPolicy from '../../common/inputs/customs/moderation-mode';
import RejectedCommentsCount from '../rejected-comments';
import PendingCommentsCount from '../autopending-comments';
import editSpotSettings from '../../../actions/spot/edit-settings';
// import FAQ from '../../common/faqs/moderation';
import MultipleModeratorsMode from './multiple-moderators-mode';
import UppercaseEnabled from './uppercase-messages-enabled';
import RestrictedUNMode from './restricted-un-mode';
import InConversationModeration from './in-conversation-moderation';
import { ModerationFiltersSettings } from 'app/scripts/components/settings/policy/moderation-filters';
import { UsernameModeration } from 'app/scripts/components/moderation/preferences/UsernameModeration';
import { AIModerationAssistSetting } from 'app/scripts/components/moderation/preferences/AIModerationAssistSetting/AIModerationAssistSetting';
import { getSpotConfig, setSpotConfig } from '@host-panel/request/config';
import { get } from 'lodash-es';
import { sendEventHOC } from 'app/scripts/tracking/sendEventHOC';
import { createSettingsChangedPayload } from 'app/scripts/components/moderation/preferences/settings-send-event-utils';
import styles from './preferences.module.scss';
import { connect } from 'react-redux';
import { openModal } from 'app/scripts/components/moderation-v2/comments-and-posts/modals/state/actions';
import { createTwoButtonsRenderActions } from 'app/scripts/components/moderation-v2/sharable/components/modals/ConfirmationModal/confirmationModalUtils';
import { ModalType } from 'app/scripts/components/moderation-v2/comments-and-posts/modals';
import { withEditSpot } from '../../../../spots/hoc/withEditSpot';

const CommunityGuidelines = React.lazy(() => import('./community-guidelines'));

const AI_MODERATION_ASSISTANCE_ENABLED = 'tenant.moderation.ai_moderation_assistance_enabled';

class ModerationPreferences extends Component {
  constructor(props, context) {
    super(props, context);
    const { spot } = this.props;

    var forceRegister = spot && !spot.forceRegister;
    var enableGuestVoting = spot && spot.allowGuestsToLike;
    var isRogerMode = spot && spot.isRogerMode;
    var isMultipleModeratorsMode = spot && spot.isMultipleModeratorsMode;
    var isRestrictedUNsEnabled = spot && spot.isRestrictedUNsEnabled;
    let uppercaseMsgsEnabled = spot && spot.uppercaseMsgsEnabled;
    let blockReportByRegistered = spot && spot.blockReportByRegistered;

    this.state = {
      isGuestCommenting: forceRegister,
      enableGuestVoting,
      isRogerMode,
      isMultipleModeratorsMode,
      isRestrictedUNsEnabled,
      uppercaseMsgsEnabled,
      allowGuestsToReport: !blockReportByRegistered,
      isAIModerationAssistanceEnabled: false
    };
  }

  createSendEvent(payload) {
    return isSuccess => {
      this.props.sendEvent({ ...payload, be_success: isSuccess });
    };
  }

  @autobind
  maybeHandleGuestReporting(value) {
    if (value) {
      this.props.openGuestReportingModal(() => this.handleGuestReporting(value));
    } else {
      this.handleGuestReporting(value);
    }
  }

  @autobind
  handleGuestCommenting(value) {
    AnalyticsProvider.track('allow guests to post clicked', { value: value });

    this.setState({ isGuestCommenting: value });
    this.context.executeAction(editSpotSettings, {
      spotId: this.props.spot.id,
      forceRegister: !value,
      sendEvent: this.createSendEvent(createSettingsChangedPayload('Allow guest commenting', value))
    });
  }

  @autobind
  handleGuestVoting(value) {
    AnalyticsProvider.track('allow guests to vote clicked', { value: value });
    this.setState({ enableGuestVoting: value });
    this.context.executeAction(editSpotSettings, {
      spotId: this.props.spot.id,
      allowGuestsToLike: value,
      sendEvent: this.createSendEvent(createSettingsChangedPayload('Allow guest voting', value))
    });
  }

  @autobind
  handleGuestReporting(value) {
    AnalyticsProvider.track('allow guests to report comments', { value: value });

    this.setState({ allowGuestsToReport: value });
    this.context.executeAction(editSpotSettings, {
      spotId: this.props.spot.id,
      blockReportByRegistered: !value,
      sendEvent: this.createSendEvent(createSettingsChangedPayload('Allow guest reporting', value))
    });
  }

  @autobind
  handleRogerMode(value) {
    this.setState({ isRogerMode: value });
    this.context.executeAction(editSpotSettings, {
      spotId: this.props.spot.id,
      rogerMode: value,
      sendEvent: be_success => {
        this.props.sendEvent({
          type: 'Moderation_ux_selected',
          element: {
            experience_selected: value ? 'Bozo' : 'Clarity'
          },
          be_success
        });
      }
    });
  }

  @autobind
  handleMultipleModeratorsMode(value) {
    AnalyticsProvider.track('multiple moderators mode clicked', { value: value });

    this.setState({ isMultipleModeratorsMode: value });

    const sendEvent = this.createSendEvent(createSettingsChangedPayload('Multiple moderators mode', value));
    this.props.editSpot(this.props.spot.id, { multiple_moderators_mode: value }, sendEvent);
  }

  @autobind
  handleRestrictedUNMode(value) {
    this.setState({ isRestrictedUNsEnabled: value });

    this.props.editSpot(this.props.spot.id, { user_moderation_enabled: value });
  }

  @autobind
  handleUppercase(value) {
    AnalyticsProvider.track('tag uppercase clicked', { value: value });

    this.setState({ uppercaseMsgsEnabled: value });

    const sendEvent = this.createSendEvent(createSettingsChangedPayload('Tag uppercase letter', value));
    this.props.editSpot(this.props.spot.id, { uppercase_msgs_enabled: value }, sendEvent);
  }

  @autobind
  async handleAIModerationAssistance(value) {
    this.setState({ isAIModerationAssistanceEnabled: value });

    let be_success = true;
    try {
      await setSpotConfig(this.props.spot.id, { [AI_MODERATION_ASSISTANCE_ENABLED]: value });
    } catch (e) {
      be_success = false;
    }

    this.props.sendEvent({
      type: value ? 'click_ai_moderation_on' : 'click_ai_moderation_off',
      source: 'ai_moderation',
      be_success
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    var { spot } = this.props;

    if (spot !== nextProps.spot && nextProps.spot) {
      this.setState({
        isGuestCommenting: !nextProps.spot.forceRegister,
        enableGuestVoting: nextProps.spot.allowGuestsToLike,
        allowGuestsToReport: !nextProps.spot.blockReportByRegistered,
        isRogerMode: nextProps.spot.isRogerMode,
        isMultipleModeratorsMode: nextProps.spot.isMultipleModeratorsMode,
        uppercaseMsgsEnabled: nextProps.spot.uppercaseMsgsEnabled
      });
    }
  }

  async componentDidMount() {
    const response = await getSpotConfig(this.props.spot.id, [AI_MODERATION_ASSISTANCE_ENABLED]);

    const value = get(response, `${AI_MODERATION_ASSISTANCE_ENABLED}.value`) ?? false;

    this.setState({ isAIModerationAssistanceEnabled: value });
  }

  render() {
    var { spot, currentUser } = this.props;
    var {
      isGuestCommenting,
      enableGuestVoting,
      isRogerMode,
      isMultipleModeratorsMode,
      isRestrictedUNsEnabled,
      uppercaseMsgsEnabled,
      allowGuestsToReport,
      isAIModerationAssistanceEnabled
    } = this.state;
    return (
      <div className="preferences comments-moderation">
        <div>
          <h4 className={styles.title}>Guest Commenting</h4>
          <Checkbox
            defaultChecked={isGuestCommenting}
            containerClassName={styles.checkbox}
            label="Allow guests to post comments."
            onChange={this.handleGuestCommenting}
          />
          <Checkbox
            defaultChecked={enableGuestVoting}
            containerClassName={styles.checkbox + ' ' + styles.marginTop10}
            label="Allow guests to up/down vote comments."
            onChange={this.handleGuestVoting}
          />
          <Checkbox
            checked={allowGuestsToReport}
            containerClassName={styles.checkbox + ' ' + styles.marginTop10}
            label="Allow guests to report comments."
            onChange={this.maybeHandleGuestReporting}
          />
          <ModerationMainPolicy spot={spot} isAIModerationAssistanceEnabled={isAIModerationAssistanceEnabled} />
          <InConversationModeration
            isSuperAdmin={currentUser.isSuperAdmin}
            isShadowMode={isRogerMode}
            handleShadowMode={this.handleRogerMode}
          />
          <ModerationFiltersSettings
            spotId={spot.id}
            isSuperAdmin={currentUser.isSuperAdmin}
            isAIModerationAssistanceEnabled={isAIModerationAssistanceEnabled}
            handleAIModerationAssistance={this.handleAIModerationAssistance}
          />
          <AIModerationAssistSetting
            isEnabled={isAIModerationAssistanceEnabled}
            onChange={this.handleAIModerationAssistance}
            moderationDefaultPolicy={spot.defaultPolicy}
          />
          {!spot.ssoEnabled && <UsernameModeration />}
          <MultipleModeratorsMode
            spotId={spot.id}
            isMultipleModeratorsMode={isMultipleModeratorsMode}
            onChange={this.handleMultipleModeratorsMode}
          />
          <UppercaseEnabled
            spotId={spot.id}
            uppercaseMsgsEnabled={uppercaseMsgsEnabled}
            onChange={this.handleUppercase}
          />
          <RestrictedUNMode
            spotId={spot.id}
            isRestrictedUNMode={isRestrictedUNsEnabled}
            onChange={this.handleRestrictedUNMode}
          />
          <RejectedCommentsCount spotId={spot.id} />
          <PendingCommentsCount spotId={spot.id} />
          <Suspense fallback={<div className="spinner" />}>
            <CommunityGuidelines spotId={spot.id} spotName={spot.name} />
          </Suspense>
        </div>
        {/*<FAQ />*/}
      </div>
    );
  }
}

ModerationPreferences.displayName = 'ModerationPreferences';

ModerationPreferences.propTypes = {
  spot: PropTypes.object,
  currentUser: PropTypes.object,
  sendEvent: PropTypes.func.isRequired,
  openGuestReportingModal: PropTypes.func.isRequired
};

ModerationPreferences.contextTypes = {
  executeAction: PropTypes.func.isRequired
};

const mapDispatchToProps = dispatch => ({
  openGuestReportingModal: onConfirm => {
    const modalProps = {
      title: 'Confirm Action',
      description:
        'Guest users can report comments, but they will not receive email updates about the report status. Enable guest reporting if this is intended.',
      renderActions: createTwoButtonsRenderActions({
        primaryButtonOnClick: () => {
          onConfirm();
        },
        primaryButtonLabel: 'Confirm'
      })
    };

    dispatch(openModal({ modalType: ModalType.CONFIRM_GENERAL, modalProps }));
  }
});

const ConnectedModerationPreferences = connect(null, mapDispatchToProps)(ModerationPreferences);

export default withEditSpot(sendEventHOC(ConnectedModerationPreferences));
